import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../lib/adminService';
import { useAuthStore } from '../../store/authStore';
import { AdminNavigation } from '../../components/AdminNavigation';
import {
  Users,
  Briefcase,
  FileText,
  DollarSign,
  TrendingUp,
  Activity,
  Zap,
  Target,
  Shield,
  // Settings, // TODO: Add settings page link in future iteration
} from 'lucide-react';

export function AdminDashboard() {
  const { profile } = useAuthStore();
  const [metrics, setMetrics] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      console.log('[AdminDashboard] Loading dashboard for admin:', profile.id);
      loadDashboardData();
    } else {
      console.warn('[AdminDashboard] Profile not available yet');
      setLoading(false);
    }
  }, [profile]);

  const loadDashboardData = async () => {
    if (!profile?.id) {
      console.warn('[AdminDashboard] Cannot load dashboard without profile');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('[AdminDashboard] Fetching metrics and activity...');
      const [metricsData, activityData] = await Promise.all([
        adminService.getPlatformMetrics(),
        adminService.getRecentActivity(10),
      ]);
      console.log('[AdminDashboard] Data loaded successfully');
      setMetrics(metricsData);
      setRecentActivity(activityData);
    } catch (error) {
      console.error('[AdminDashboard] Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Users',
      value: metrics?.total_users || 0,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      link: '/admin/users',
    },
    {
      label: 'Job Seekers',
      value: metrics?.total_job_seekers || 0,
      icon: Target,
      color: 'from-green-500 to-green-600',
      link: '/admin/users?role=job_seeker',
    },
    {
      label: 'Employers',
      value: metrics?.total_employers || 0,
      icon: Briefcase,
      color: 'from-orange-500 to-amber-500',
      link: '/admin/users?role=employer',
    },
    {
      label: 'Active Jobs',
      value: metrics?.total_jobs_active || 0,
      icon: FileText,
      color: 'from-cyan-500 to-blue-500',
      link: '/admin/jobs',
    },
    {
      label: 'Applications',
      value: metrics?.total_applications || 0,
      icon: Activity,
      color: 'from-pink-500 to-rose-500',
      link: '/admin/applications',
    },
    {
      label: 'Total Revenue',
      value: `$${metrics?.total_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'from-emerald-500 to-green-600',
      link: '/admin/financials',
    },
  ];

  const quickLinks = [
    {
      title: 'User Management',
      description: 'View and manage all platform users',
      icon: Users,
      link: '/admin/users',
      color: 'bg-blue-500',
    },
    {
      title: 'Financial Dashboard',
      description: 'Track revenue and transactions',
      icon: DollarSign,
      link: '/admin/financials',
      color: 'bg-green-500',
    },
    {
      title: 'Analytics',
      description: 'Lean Startup metrics and insights',
      icon: TrendingUp,
      link: '/admin/analytics',
      color: 'bg-orange-500',
    },
    {
      title: 'Feature Flags',
      description: 'Control feature rollouts',
      icon: Zap,
      link: '/admin/features',
      color: 'bg-cyan-500',
    },
  ];

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 ml-64">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold mb-2">Master Admin Dashboard</h1>
              <p className="text-blue-100">
                Welcome back, {profile?.full_name || 'Admin'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition"
              >
                View Site
              </Link>
              <Shield className="w-12 h-12" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat) => (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-transparent hover:border-blue-300"
            >
              <div
                className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${stat.color} mb-4`}
              >
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-orange-600" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {quickLinks.map((link) => (
                <Link
                  key={link.title}
                  to={link.link}
                  className="p-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition group"
                >
                  <div
                    className={`w-10 h-10 ${link.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}
                  >
                    <link.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {link.title}
                  </h3>
                  <p className="text-xs text-gray-600">{link.description}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-6 h-6 text-blue-600" />
              Recent Activity
            </h2>
            {recentActivity.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No recent activity</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentActivity.map((activity: any) => (
                  <div
                    key={activity.id}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-semibold text-sm text-gray-900">
                        {activity.action_type.replace(/_/g, ' ').toUpperCase()}
                      </p>
                      <span className="text-xs text-gray-500">
                        {new Date(activity.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      {activity.admin?.full_name || 'Admin'} modified{' '}
                      {activity.target_type}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">
                Lean Startup Principles
              </h2>
              <p className="text-white/90 mb-4">
                Focus on actionable metrics, validated learning, and rapid
                experimentation
              </p>
              <div className="flex gap-4 text-sm">
                <div>
                  <p className="font-semibold">Build</p>
                  <p className="text-white/80">Minimal Viable Product</p>
                </div>
                <div>
                  <p className="font-semibold">Measure</p>
                  <p className="text-white/80">User Engagement</p>
                </div>
                <div>
                  <p className="font-semibold">Learn</p>
                  <p className="text-white/80">Iterate & Pivot</p>
                </div>
              </div>
            </div>
            <Link
              to="/admin/analytics"
              className="px-6 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              View Analytics
            </Link>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
